import {
  fetchInstoreTemplatesAsync,
  instoreTemplatesSelectors,
} from "../features/instoreTemplates/instoreTemplatesSlice";
import { useAppDispatch, useAppSelector } from "../store/configureStore";
import { isWithinInterval, parse } from "date-fns";
import { useEffect } from "react";
import { InstoreTemplate } from "../models/instoreTemplate";

const STATIC_INSTORE_ORDER_TYPE_NAME = "Week";

// Returns the most recent active "Week" instore template whose date window
// covers today. Multiple may match (sloppy app usage) — pick the one with the
// most recent `from`, tiebreak on most recent `created_at`.
export default function useActiveWeekInstoreTemplate():
  | InstoreTemplate
  | undefined {
  const dispatch = useAppDispatch();
  const { instoreTemplates_Loaded } = useAppSelector(
    (state) => state.instoreTemplates
  );
  const instoreTemplates = useAppSelector(instoreTemplatesSelectors.selectAll);

  useEffect(() => {
    if (!instoreTemplates_Loaded) {
      dispatch(fetchInstoreTemplatesAsync());
    }
  }, [instoreTemplates_Loaded, dispatch]);

  type Match = {
    template: InstoreTemplate;
    fromTime: number;
    createdAtTime: number;
  };

  const today = new Date();
  const matches = instoreTemplates
    .map<Match | null>((template) => {
      if (!template.active) return null;
      if (template.order_type_instore_name !== STATIC_INSTORE_ORDER_TYPE_NAME)
        return null;
      if (!template.from || !template.until) return null;
      const from = parse(template.from, "dd/MM/yyyy HH:mm", new Date());
      const until = parse(template.until, "dd/MM/yyyy HH:mm", new Date());
      if (isNaN(from.getTime()) || isNaN(until.getTime())) return null;
      if (!isWithinInterval(today, { start: from, end: until })) return null;
      const createdAt = template.created_at
        ? new Date(template.created_at).getTime()
        : 0;
      return {
        template,
        fromTime: from.getTime(),
        createdAtTime: isNaN(createdAt) ? 0 : createdAt,
      };
    })
    .filter((m): m is Match => m !== null);

  matches.sort((a, b) => {
    if (b.fromTime !== a.fromTime) return b.fromTime - a.fromTime;
    return b.createdAtTime - a.createdAtTime;
  });

  return matches[0]?.template;
}
